// frontend/src/pages/DashboardPage.js
// Componente de la página principal del Dashboard.
// ACTUALIZADO: Textos adaptados a la nueva identidad de la plataforma.

import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useAuth } from '../hooks/useAuth';
import { useProjectStore } from '../store/projectStore';
import ClientList from '../components/ClientList';
import ClientForm from '../components/ClientForm';
import ProjectForm from '../components/ProjectForm';
import ProjectDetails from '../components/ProjectDetails';
import OverallDashboardPage from './OverallDashboardPage';
import RevenueAnalysisPage from './RevenueAnalysisPage';
import TemplateAdminPage from './TemplateAdminPage';
import UserAdminPage from './UserAdminPage';
import BackupAdminPage from './BackupAdminPage';
import HelpModal from '../components/HelpModal';
import {
  ArrowLeftIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  ChartPieIcon,
  WrenchScrewdriverIcon,
  UserGroupIcon,
  CircleStackIcon,
  QuestionMarkCircleIcon,
  BuildingOffice2Icon,
  BriefcaseIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/solid';

/**
 * @component DashboardPage
 * @description Página principal que muestra la información general y permite navegar a las diferentes secciones de gestión.
 */
function DashboardPage() {
  const { user } = useAuthStore((state) => ({ user: state.user }));
  const { logout } = useAuth();
  const { selectedProject, setSelectedProject } = useProjectStore();

  const [currentView, setCurrentView] = useState('dashboard');
  const [clientToEdit, setClientToEdit] = useState(null);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (currentView !== 'projectDetails') {
      setSelectedProject(null);
    }
  }, [currentView, setSelectedProject]);

  const showDashboardOverview = () => {
    setCurrentView('dashboard');
    setClientToEdit(null);
    setProjectToEdit(null);
  };

  const showOverallDashboard = () => setCurrentView('overallDashboard');
  const showRevenueAnalysis = () => setCurrentView('revenueAnalysis');
  
  const showClientList = () => setCurrentView('clientList');
  const showAddClientForm = () => {
    setClientToEdit(null);
    setCurrentView('addClient');
  };
  const showEditClientForm = (client) => {
    setClientToEdit(client);
    setCurrentView('editClient');
  };

  const showAddProjectForm = () => {
    setProjectToEdit(null);
    setCurrentView('addProject');
  };
  const showEditProjectForm = (project) => {
    setProjectToEdit(project);
    setCurrentView('editProject');
  };
  const showProjectDetailsView = (project) => {
    setSelectedProject(project);
    setCurrentView('projectDetails');
  };
  
  const showTemplateAdmin = () => setCurrentView('templateAdmin');
  const showUserAdmin = () => setCurrentView('userAdmin');
  const showBackupAdmin = () => setCurrentView('backupAdmin');

  const primaryNavigation = [
    { key: 'dashboard', label: 'Inicio', icon: <HomeIcon />, onClick: showDashboardOverview },
    { key: 'overallDashboard', label: 'Iniciativas', icon: <ChartPieIcon />, onClick: showOverallDashboard },
    { key: 'clientList', label: 'Clientes', icon: <BuildingOffice2Icon />, onClick: showClientList },
    { key: 'addProject', label: 'Nueva iniciativa', icon: <BriefcaseIcon />, onClick: showAddProjectForm },
  ];

  const adminNavigation = [
    { key: 'templateAdmin', label: 'Plantillas', icon: <WrenchScrewdriverIcon />, onClick: showTemplateAdmin },
    { key: 'userAdmin', label: 'Usuarios', icon: <UserGroupIcon />, onClick: showUserAdmin },
    { key: 'backupAdmin', label: 'Backups', icon: <CircleStackIcon />, onClick: showBackupAdmin },
  ];

  const NavButton = ({ item }) => {
    const isActive = currentView === item.key;

    return (
      <button
        type="button"
        onClick={item.onClick}
        className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
          isActive
            ? 'bg-slate-900 text-white shadow-sm'
            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
        }`}
      >
        {React.cloneElement(item.icon, { className: 'h-5 w-5 flex-none' })}
        <span>{item.label}</span>
      </button>
    );
  };

  const NavigationCard = ({ title, description, icon, onClick, colorClass = 'gray' }) => {
    const colors = {
      green: { bg: 'bg-green-100', text: 'text-green-700', shadow: 'hover:shadow-green-500/20' },
      indigo: { bg: 'bg-indigo-100', text: 'text-indigo-700', shadow: 'hover:shadow-indigo-500/20' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-700', shadow: 'hover:shadow-purple-500/20' },
      slate: { bg: 'bg-slate-100', text: 'text-slate-700', shadow: 'hover:shadow-slate-500/20' },
      amber: { bg: 'bg-amber-100', text: 'text-amber-700', shadow: 'hover:shadow-amber-500/20' },
      teal: { bg: 'bg-teal-100', text: 'text-teal-700', shadow: 'hover:shadow-teal-500/20' },
    };
    const selectedColor = colors[colorClass];

    return (
      <button
        onClick={onClick}
        className={`group relative flex flex-col items-start text-left bg-white p-6 rounded-xl shadow-md border border-gray-200/80 transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-xl ${selectedColor.shadow}`}
      >
        <div className="flex-shrink-0">
          <span className={`inline-flex items-center justify-center h-12 w-12 rounded-lg ${selectedColor.bg} ${selectedColor.text}`}>
            {React.cloneElement(icon, { className: "h-6 w-6" })}
          </span>
        </div>
        <div className="mt-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="mt-1 text-sm text-gray-600">{description}</p>
        </div>
        <ChevronRightIcon className="absolute top-6 right-6 h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-100 lg:flex">
      <aside className="hidden w-72 flex-col border-r border-slate-200 bg-white lg:flex">
        <div className="flex h-16 items-center border-b border-slate-200 px-5">
          <img
            src="https://noventiq.com/images/svg/noventiq/logo.svg"
            alt="Noventiq Logo"
            className="h-7 w-auto"
          />
        </div>
        <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-4 py-5">
          <div>
            <p className="px-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Operación</p>
            <div className="mt-2 space-y-1">
              {primaryNavigation.map((item) => <NavButton key={item.key} item={item} />)}
            </div>
          </div>

          {isAdmin && (
            <div>
              <p className="px-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Administración</p>
              <div className="mt-2 space-y-1">
                {adminNavigation.map((item) => <NavButton key={item.key} item={item} />)}
              </div>
            </div>
          )}
        </div>
        <div className="border-t border-slate-200 p-4">
          <div className="rounded-md bg-slate-50 p-3">
            <p className="truncate text-sm font-semibold text-slate-900">{user?.displayName || user?.upn || user?.username}</p>
            <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">{user?.role || 'user'}</p>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur md:px-8">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Plataforma de Iniciativas AWS</p>
              <h1 className="truncate text-lg font-bold text-slate-950 md:text-xl">Seguimiento comercial y operativo</h1>
            </div>
            <div className="flex items-center gap-2">
              {currentView !== 'dashboard' && (
                <button
                  type="button"
                  onClick={showDashboardOverview}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  <ArrowLeftIcon className="h-5 w-5" />
                  <span className="hidden sm:inline">Inicio</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowHelpModal(true)}
                className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
              >
                <QuestionMarkCircleIcon className="h-5 w-5" />
                <span className="hidden sm:inline">Ayuda</span>
              </button>
              <button
                type="button"
                onClick={logout}
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                <span className="hidden sm:inline">Salir</span>
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8">
        {currentView === 'dashboard' && (
          <div className="max-w-7xl mx-auto">
            <div className="mb-8 rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-sm font-medium text-slate-500">Hola, <span className="font-semibold text-slate-900">{user?.displayName || user?.upn || user?.username}</span></p>
              <h2 className="mt-2 text-3xl font-bold text-slate-950">Centro de control</h2>
              <p className="mt-2 max-w-3xl text-sm text-slate-600">Gestiona iniciativas, clientes, reportes y administracion desde un espacio unico orientado a seguimiento diario.</p>
            </div>

            <div className="space-y-10">
              <section>
                <h3 className="text-xl font-bold text-gray-800 mb-4 px-1">Gestión Principal</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <NavigationCard 
                    title="Dashboard de Iniciativas" 
                    description="Visualiza métricas, filtra y gestiona todas las iniciativas desde un solo lugar."
                    icon={<ChartPieIcon />}
                    onClick={showOverallDashboard}
                    colorClass="green"
                  />
                  <NavigationCard 
                    title="Gestión de Clientes"
                    description="Añade, edita o elimina clientes y organizaciones de tu portafolio."
                    icon={<BuildingOffice2Icon />}
                    onClick={showClientList}
                    colorClass="indigo"
                  />
                   <NavigationCard 
                    title="Añadir Nueva Iniciativa"
                    description="Inicia un nuevo proyecto MAP, PoC o de Implementación para un cliente."
                    icon={<BriefcaseIcon />}
                    onClick={showAddProjectForm}
                    colorClass="purple"
                  />
                </div>
              </section>

              {isAdmin && (
                <section>
                  <h3 className="text-xl font-bold text-gray-800 mb-4 px-1">Panel de Administración</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <NavigationCard 
                      title="Gestión de Plantillas"
                      description="Configura las fases, tareas y documentos para cada tipo de iniciativa."
                      icon={<WrenchScrewdriverIcon />}
                      onClick={showTemplateAdmin}
                      colorClass="slate"
                    />
                    <NavigationCard 
                      title="Gestión de Usuarios"
                      description="Crea nuevos usuarios, asigna roles y administra los accesos a la plataforma."
                      icon={<UserGroupIcon />}
                      onClick={showUserAdmin}
                      colorClass="amber"
                    />
                    <NavigationCard 
                      title="Gestión de Backups"
                      description="Realiza copias de seguridad o restaura toda la información de la aplicación."
                      icon={<CircleStackIcon />}
                      onClick={showBackupAdmin}
                      colorClass="teal"
                    />
                  </div>
                </section>
              )}
            </div>
          </div>
        )}

        {/* Renderizado de las otras vistas */}
        {isAdmin && currentView === 'userAdmin' && <UserAdminPage onBack={showDashboardOverview} />}
        {isAdmin && currentView === 'templateAdmin' && <TemplateAdminPage onBack={showDashboardOverview} />}
        {isAdmin && currentView === 'backupAdmin' && <BackupAdminPage onBack={showDashboardOverview} />}
        
        {currentView === 'overallDashboard' && 
            <OverallDashboardPage 
                onViewRevenueAnalysis={showRevenueAnalysis} 
                onAddProject={showAddProjectForm}
                onEditProject={showEditProjectForm}
                onViewProjectDetails={showProjectDetailsView}
            />
        }
        
        {currentView === 'revenueAnalysis' && <RevenueAnalysisPage onBack={showOverallDashboard} />}
        
        {currentView === 'clientList' && <ClientList onAddClient={showAddClientForm} onEditClient={showEditClientForm} />}
        {(currentView === 'addClient' || currentView === 'editClient') && <ClientForm clientToEdit={clientToEdit} onSuccess={showClientList} onCancel={showClientList} />}
        
        {(currentView === 'addProject' || currentView === 'editProject') && 
            <ProjectForm 
                projectToEdit={projectToEdit} 
                onSuccess={showOverallDashboard} 
                onCancel={showOverallDashboard}
            />
        }
        
        {currentView === 'projectDetails' && selectedProject && 
            <ProjectDetails 
                projectId={selectedProject.id} 
                onBack={showOverallDashboard}
            />
        }
        </main>
      </div>

      <HelpModal show={showHelpModal} onClose={() => setShowHelpModal(false)} />
    </div>
  );
}

export default DashboardPage;
